"use client";

import { c as _c } from "react/compiler-runtime";
import { useT } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import { cachedFetch } from "@/lib/fetch-cache";
import { useBetaPrefix } from "@/lib/api/prefix.client";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
interface Change {
  field: string;
  old: string;
  new: string;
}
interface HistoryEntry {
  version: string;
  date: string;
  action: "added" | "removed" | "changed";
  changes: Change[];
}
interface EntityHistoryProps {
  entityType: string;
  entityId: string;
}
const actionColors: Record<string, string> = {
  added: "text-success",
  removed: "text-danger",
  changed: "text-warning"
};
const actionLabels: Record<string, string> = {
  added: "Added",
  removed: "Removed",
  changed: "Changed"
};
const dotColors: Record<string, string> = {
  added: "bg-success-fill",
  removed: "bg-danger-fill",
  changed: "bg-warning-fill"
};

/**
 * Pre-deep-diff changelogs (v1.0.x) stored nested data as stringified
 * Python dict / list literals like `{'id': 'BASH', 'name': 'Bash'}`. Try
 * to parse those into real JSON so we can render them as readable lines
 * instead of one long unreadable blob. Returns the original string when
 * it doesn't look parsable.
 */
function tryParsePythonish(s: string): unknown {
  if (typeof s !== "string") return s;
  const trimmed = s.trim();
  if (!trimmed) return s;
  // Heuristic: look like a dict/list literal or a comma-joined sequence
  // of dict literals (multi-item list rendered as `{...}, {...}`).
  const dictish = /^[{\[]/.test(trimmed);
  const looksLikeMultiDict = /^\{.*\},\s*\{/.test(trimmed);
  if (!dictish && !looksLikeMultiDict) return s;
  // Wrap a comma-joined dict sequence in [] so it's a JSON array.
  const wrapped = looksLikeMultiDict && !trimmed.startsWith("[") ? `[${trimmed}]` : trimmed;
  // Convert single quotes -> double quotes, Python None/True/False -> json
  const json = wrapped.replace(/'/g, '"').replace(/\bNone\b/g, "null").replace(/\bTrue\b/g, "true").replace(/\bFalse\b/g, "false");
  try {
    return JSON.parse(json);
  } catch {
    return s;
  }
}
function ChangeValue(t0) {
  const $ = _c(11);
  const {
    raw,
    color
  } = t0;
  if (raw === "none" || raw === "null" || !raw) {
    const t1 = raw || "\u2014";
    let t2;
    if ($[0] !== color || $[1] !== t1) {
      t2 = <span className={color}>{t1}</span>;
      $[0] = color;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  let t2;
  let t3;
  if ($[3] !== color || $[4] !== raw) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const parsed = tryParsePythonish(raw);
      if (parsed === raw || typeof parsed === "string") {
        t3 = <span className={color}>{raw}</span>;
        break bb0;
      }
      t1 = `${color} text-[10px] leading-snug whitespace-pre-wrap font-mono break-words`;
      t2 = JSON.stringify(parsed, null, 2);
    }
    $[3] = color;
    $[4] = raw;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2) {
    t4 = <pre className={t1}>{t2}</pre>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
export default function EntityHistory(t0) {
  const $ = _c(21);
  const {
    entityType,
    entityId
  } = t0;
  const t = useT();
  const bp = useBetaPrefix();
  const [history, setHistory] = useState(null);
  let t1;
  let t2;
  if ($[0] !== entityId || $[1] !== entityType) {
    t1 = () => {
      cachedFetch(`${API}/api/history/${entityType}/${entityId}`).then(setHistory);
    };
    t2 = [entityType, entityId];
    $[0] = entityId;
    $[1] = entityType;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== t) {
    t3 = t("Version history");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <h2>{t3}</h2>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== bp || $[9] !== history || $[10] !== t) {
    t5 = history && history.length > 0 ? <div className="relative ml-2"><div className="absolute left-[5px] top-2 bottom-2 w-px bg-[var(--border-subtle)]" /><div className="space-y-4">{history.map((entry, i) => <div key={`${entry.version}-${i}`} className="relative pl-6"><div className={`absolute left-0 top-1.5 w-[11px] h-[11px] rounded-full border-2 border-[var(--bg-primary)] ${dotColors[entry.action] || "bg-line-strong"}`} /><div><div className="flex items-center gap-2 text-xs"><Link href={`${bp}/changelog#${entry.version}`} className="font-semibold text-[var(--text-primary)] hover:text-[var(--accent-gold)] transition-colors">v{entry.version}</Link><span className={actionColors[entry.action] || "text-fg-muted"}>{actionLabels[entry.action] ? t(actionLabels[entry.action]) : entry.action}</span><span className="text-[var(--text-muted)]">{entry.date}</span></div>{entry.changes.length > 0 && <div className="mt-1.5 space-y-2">{entry.changes.map((change, j) => {
                const oldStr = String(change.old ?? "");
                const newStr = String(change.new ?? "");
                const isComplex = oldStr.length + newStr.length > 80 || oldStr.startsWith("{") || oldStr.startsWith("[") || newStr.startsWith("{") || newStr.startsWith("[");
                if (isComplex) {
                  return <div key={`${change.field}-${j}`} className="text-xs text-[var(--text-muted)]"><div className="text-[var(--text-secondary)] font-medium mb-1">{change.field}</div><div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-3"><div><div className="text-[10px] uppercase tracking-wider text-danger/70 mb-0.5">{t("Before")}</div><ChangeValue raw={oldStr} color="text-danger/80" /></div><div><div className="text-[10px] uppercase tracking-wider text-success/70 mb-0.5">{t("After")}</div><ChangeValue raw={newStr} color="text-success/80" /></div></div></div>;
                }
                return <div key={`${change.field}-${j}`} className="text-xs text-[var(--text-muted)] flex items-baseline gap-1.5 flex-wrap"><span className="text-[var(--text-secondary)] font-medium">{change.field}</span><span className="text-danger/70 line-through">{oldStr}</span><span className="text-[var(--text-muted)]">→</span><span className="text-success/70">{newStr}</span></div>;
              })}</div>}</div></div>)}</div></div> : history && history.length === 0 ? <p className="text-xs text-[var(--text-muted)] m-0">{t("No version history recorded for this entity.")}</p> : <p className="text-xs text-[var(--text-muted)] m-0">{t("Loading\u2026")}</p>;
    $[8] = bp;
    $[9] = history;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = `${bp}/changelog`;
  let t7;
  if ($[12] !== t) {
    t7 = t("View the full changelog");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <Link href={t6} className="mt-4 inline-block text-xs text-[var(--text-secondary)] hover:text-[var(--accent-gold)] transition-colors">{t7} →</Link>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t8) {
    t9 = <section id="history">{t4}{t5}{t8}</section>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}